// Types and the few pure helpers the Grievances page needs. No JSX and no
// imports, so web/test/grievances.test.ts can load it under Node's own runner.
//
// Nothing here decides anything about a grievance. Who holds it, when it is
// due, whether it is overdue and what the viewer may see all come from Postgres
// (db/migrations/0720 to 0730). The page arranges them; these helpers only
// group, label and check that a form is filled before it is sent, so the
// refusal a person meets first is the same one Postgres would give.

export type Row = {
  id: string
  ref: string
  full: boolean
  confidential: boolean
  raised_at: string
  stage: string
  stage_label: string
  status: 'open' | 'answered' | 'accepted' | 'withdrawn'
  meet_by: string | null
  answer_by: string | null
  met_on: string | null
  overdue: boolean
  meeting_overdue: boolean
  holder: string | null
  holder_is_me: boolean
  collective: boolean
  collective_parent_id: string | null
  archived: boolean
  is_test: boolean
  late: boolean | null
  employee: string | null
  driver_id: string | null
  category: string | null
  category_label: string | null
  channel: string | null
  urgent: boolean | null
  harassment: boolean | null
  ccma_referred_on: string | null
}

export type Summary = {
  from: string
  to: string
  raised: number
  by_category: { category: string; label: string; count: number }[]
  resolved: number
  median_days_to_resolve: number | null
  overdue: number
  escalations: number
  ccma_referrals: number
  confidential: number
  open_now: number
  overdue_now: number
}

export type ListResult = {
  me: { person_id: string; name: string | null; is_operational_servant: boolean; is_chief_servant: boolean }
  roles: {
    operational_servant: string | null
    chief_servant: string | null
    board_chair: string | null
    nominated_director: string | null
    mailbox: string | null
    chair_mailbox: string | null
    mail_enabled: boolean
  }
  counts: { open: number; overdue: number; mine: number; answered_awaiting_him: number; this_year: number }
  open: Row[]
  register: Row[]
  reports: { kind: 'monthly_review' | 'quarterly_board'; from: string; to: string; summary: Summary; task_id: string | null }[]
  this_quarter: Summary
}

export type Attachment = { bucket?: string; path?: string; filename?: string; mime?: string | null; kind?: string; body?: string; type?: string; from?: string }

export type Detail = {
  row: Row
  restricted: boolean
  message?: string
  description?: string
  event_date?: string | null
  language?: string | null
  subject?: string | null
  subject_level?: string
  route_rule?: string | null
  route_reason?: string | null
  harassment_route?: string | null
  late_explanation?: string | null
  late_accepted?: boolean | null
  read_back_confirmed?: boolean | null
  recorded_by?: string | null
  acknowledged_at?: string | null
  task_id?: string | null
  escalated_from?: string | null
  employee_email?: string | null
  dispute_matter_id?: string | null
  events?: { id: number; at: string; stage: string | null; type: string; who: string | null; what: string | null; attachments: Attachment[]; message_id: string | null; message_status: string | null; task_id: string | null }[]
  answers?: { id: string; stage: string; sent_at: string; decider: string; meeting_on: string | null; companion: string | null; representative: string | null; interpreter: string | null; found: string; will_do: string; will_do_by: string; next_step: string; response: string | null; responded_at: string | null }[]
  actions?: { action: string; due_on: string; task_id: string | null; task_status: string | null }[]
  parties?: { role: string; name: string; stage: string | null }[]
  joined?: { id: string; ref: string; employee: string | null }[]
  checks?: { case_kind: string; case_id: string | null; by: string | null; at: string; note: string | null }[]
  can?: { meeting: boolean; late: boolean; harassment_route: boolean; answer: boolean; reclassify: boolean; escalate_on_request: boolean; collective: boolean; withdraw: boolean; ccma: boolean }
}

export type Options = {
  categories: { value: string; label: string }[]
  employees: { person_id: string; name: string; kind: 'driver' | 'staff'; driver_status: string | null; has_email: boolean; manager: string | null }[]
  staff: { person_id: string; name: string; roles: string }[]
  whatsapp: { id: number; from: string; name: string | null; person_id: string | null; type: string; body: string; at: string }[] | null
}

export const STAGE_ORDER = ['urgent', 'harassment', 'step_one', 'step_two', 'chief_servant', 'board', 'confidential'] as const

export const STAGE_HELP: Record<string, string> = {
  step_one: 'Driver Servant or direct manager. Meet within 3 working days, answer within 5 (31.4).',
  step_two: 'Operational Servant. Meet within 5 working days, answer within 5 of the meeting (31.5, 31.6).',
  chief_servant: 'About the Operational Servant: the Chief Servant answers within 10 working days (31.7).',
  board: 'About the Chief Servant, or lodged with the chair: the board chair answers within 15 working days (31.8).',
  urgent: 'Safety, violence, or unpaid or late pay: answered within 2 working days (31.9).',
  harassment: 'Harassment: investigated confidentially under the Code of Good Practice (31.10).',
  confidential: 'Confidential matters you are not part of. Only the people deciding them can read them (31.8, 31.10).',
}

export const STATUS_LABEL: Record<Row['status'], string> = {
  open: 'Open',
  answered: 'Answered, waiting for him',
  accepted: 'He accepted',
  withdrawn: 'Withdrawn in writing',
}

export const CHANNEL_LABEL: Record<string, string> = {
  app_form: 'Grievance form',
  email: 'Email',
  whatsapp_text: 'WhatsApp text',
  whatsapp_voice: 'WhatsApp voice note',
  in_person: 'In person',
  chair_email: 'Email to the chair',
}

/** Open grievances by stage, in the order the contract escalates them, then by due date. */
export function groupByStage(rows: Row[]): { stage: string; label: string; rows: Row[] }[] {
  const out: { stage: string; label: string; rows: Row[] }[] = []
  for (const stage of STAGE_ORDER) {
    const inStage = rows.filter((r) => r.stage === stage)
    if (inStage.length === 0) continue
    inStage.sort((a, b) => String(a.answer_by ?? '').localeCompare(String(b.answer_by ?? '')))
    out.push({ stage, label: inStage[0].stage_label, rows: inStage })
  }
  return out
}

/** The holders of open grievances, with how many each holds and how many are overdue. */
export function byHolder(rows: Row[]): { holder: string; open: number; overdue: number }[] {
  const m = new Map<string, { holder: string; open: number; overdue: number }>()
  for (const r of rows) {
    const k = r.holder ?? (r.confidential ? 'Confidential' : 'Nobody')
    const e = m.get(k) ?? { holder: k, open: 0, overdue: 0 }
    e.open += 1
    if (r.overdue) e.overdue += 1
    m.set(k, e)
  }
  return [...m.values()].sort((a, b) => b.overdue - a.overdue || b.open - a.open || a.holder.localeCompare(b.holder))
}

export type AnswerForm = { meeting_on: string; found: string; will_do: string; will_do_by: string; next_step: string }

/**
 * What is still missing before the answer may be sent. The same four Postgres
 * insists on (31.12), and the meeting at the steps where he is spoken with
 * first. An empty list means the button may be pressed.
 */
export function answerMissing(f: AnswerForm, stage: string, metOn: string | null): string[] {
  const missing: string[] = []
  if (['step_one', 'step_two', 'harassment'].includes(stage) && !f.meeting_on && !metOn) missing.push('the day you met him')
  if (!f.found.trim()) missing.push('what was found')
  if (!f.will_do.trim()) missing.push('what Fleetly will do')
  if (!f.will_do_by) missing.push('by when')
  if (!f.next_step.trim()) missing.push('the next step')
  return missing
}

export function formatDay(d: string | null | undefined): string {
  if (!d) return '—'
  const x = new Date(d.length === 10 ? `${d}T12:00:00+02:00` : d)
  return x.toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Africa/Johannesburg' })
}

export function formatWhen(d: string | null | undefined): string {
  if (!d) return '—'
  return new Date(d).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Johannesburg' })
}

/** Today in Johannesburg as YYYY-MM-DD, for date inputs. */
export function todaySast(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Johannesburg', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now)
}
